// src/stores/useAuth.ts
import { create } from 'zustand'
import { supabase } from '../services/supabase'
import type { User, Session } from '@supabase/supabase-js'

interface AuthState {
  session: Session | null
  user: User | null
  loading: boolean
  passwordRecovery: boolean
  setSession: (session: Session | null) => void
  completePasswordRecovery: () => void
  signOut: () => Promise<void>
  init: () => () => void
}

export const useAuth = create<AuthState>((set) => ({
  session: null,
  user: null,
  loading: true,
  passwordRecovery: false,

  setSession: (session) => set({ session, user: session?.user ?? null, loading: false }),

  // Called after a successful `updateUser({ password })` on the reset-password
  // screen, so the app falls through to the normal dashboard instead of
  // staying stuck showing the "set new password" form forever.
  completePasswordRecovery: () => set({ passwordRecovery: false }),

  init: () => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      set({ session, user: session?.user ?? null, loading: false })
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'PASSWORD_RECOVERY') {
        // Supabase fires this (instead of SIGNED_IN) when the user lands here
        // via a password-recovery email link. Flag it so App.tsx can route
        // to the "set new password" screen instead of the normal dashboard.
        set({ session, user: session?.user ?? null, loading: false, passwordRecovery: true })
        return
      }

      if (_event === 'SIGNED_OUT') {
        set({ session: null, user: null, loading: false, passwordRecovery: false })
        return
      }

      // Any other event (SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED, etc.) means
      // we're no longer in the recovery flow — clear the flag so a stale
      // recovery session can't strand the user on the reset screen.
      set({ session, user: session?.user ?? null, loading: false, passwordRecovery: false })
    })

    return () => subscription.unsubscribe()
  },

  signOut: async () => {
    await supabase.auth.signOut()
    set({ session: null, user: null, loading: false, passwordRecovery: false })
  },
}))
